import React, {
  Fragment,
  HTMLProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import {
  errorResponse,
  residentInfiniteResponseWithEditStatus,
  residentWithMetaResponse,
} from "../types/response";
import { residentApi } from "../fetchers";
import { searchTextState, selectState, tableState } from "../store";
import Footer from "./footer";
import TableNewRow from "../components/tableNewRow";
import TableEditRow from "../components/tableEditRow";
import TablePage from "../components/tablePage";

function Table() {
  const [userData, setUserData] =
    useState<residentInfiniteResponseWithEditStatus>();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [editRowId, setEditRowId] = useState<number | undefined>();
  const [totalPage, setTotalPage] = useRecoilState(tableState.totalPage);
  const newRowData = useRecoilValue(tableState.newRowData);
  const dong = useRecoilValue(selectState.dong);
  const ho = useRecoilValue(selectState.ho);
  const area = useRecoilValue(selectState.area);
  const page = useRecoilValue(tableState.currentPage);
  const searchText = useRecoilValue(searchTextState);

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <IndeterminateCheckbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <IndeterminateCheckbox
            {...{
              checked: row.getIsSelected(),
              indeterminate: row.getIsSomeSelected(),
              onChange: row.getToggleSelectedHandler(),
            }}
          />
        ),
        size: 55,
      },
      {
        accessorKey: "dong",
        header: "동",
        size: 90,
      },
      {
        accessorKey: "ho",
        header: "호",
        size: 90,
      },
      {
        accessorKey: "name",
        header: "이름",
        size: 130,
      },
      {
        accessorKey: "birthDate",
        header: "생년월일",
        size: 150,
      },
      {
        accessorKey: "phone",
        header: "핸드폰",
        size: 180,
      },
      {
        accessorKey: "area",
        header: "선거구",
        size: 90,
      },
      {
        accessorKey: "isOwner",
        header: "소유주",
        size: 90,
      },
      {
        accessorKey: "isResident",
        header: "입주민",
        size: 90,
      },
      {
        header: "관리",
        size: 150,
        cell: (e) => {
          return (
            <Fragment>
              <span
                className={"cursor-pointer"}
                onClick={() => setEditRowId(Number(e.row.id))}
              >
                수정
              </span>
              <span
                className={"cursor-pointer text-gray-500 ml-3"}
                onClick={() => handleRemove({ id: [e.row.original.id] })}
              >
                삭제
              </span>
            </Fragment>
          );
        },
      },
    ],
    []
  );

  function IndeterminateCheckbox({
    indeterminate,
    className = "",
    ...rest
  }: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
    const ref = React.useRef<HTMLInputElement>(null!);

    useEffect(() => {
      if (typeof indeterminate === "boolean") {
        ref.current.indeterminate = !rest.checked && indeterminate;
      }
    }, [ref, indeterminate]);

    return (
      <input
        type="checkbox"
        ref={ref}
        className={className + " cursor-pointer"}
        {...rest}
      />
    );
  }

  const { fetchNextPage, isFetching, refetch } =
    useInfiniteQuery<residentWithMetaResponse>(
      ["table-data", dong, ho, area, searchText, page],
      async () =>
        await residentApi.getFiltered({
          page,
          dong,
          ho,
          area_code: area,
          search_text: searchText,
        }),
      {
        getNextPageParam: (lastPage) => lastPage.meta.page + 1,
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        onSuccess: (data: any) => {
          setTotalPage(data.pages[0].meta.totalPage);
          setUserData(data);
        },
      }
    );

  const flatData = useMemo(
    () => userData?.pages?.flatMap((page) => page.data) ?? [],
    [userData]
  );

  const totalDBRowCount = userData?.pages?.[0]?.meta?.totalCount ?? 0;
  const totalFetched = flatData.length;

  const fetchMoreOnBottomReached = React.useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        if (
          scrollHeight - scrollTop - clientHeight < 300 &&
          !isFetching &&
          totalFetched < totalDBRowCount
        ) {
          fetchNextPage();
        }
      }
    },
    [fetchNextPage, isFetching, totalFetched, totalDBRowCount]
  );

  const table = useReactTable({
    data: flatData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  const handleRemove = useCallback(async ({ id }) => {
    if (window.confirm("삭제하시겠습니까")) {
      const selectedUser: number[] = [];
      table
        .getSelectedRowModel()
        .flatRows.map((data) => selectedUser.push(data.original.id));

      try {
        let removeResponse;

        !id
          ? (removeResponse = await residentApi.remove(selectedUser))
          : (removeResponse = await residentApi.remove(id));

        if (removeResponse.data) {
          alert("삭제되었습니다");
          await refetch();
        }
      } catch (err: unknown) {
        const customError = err as errorResponse;
        alert(customError.response.data.error.message);
      }
    }
  }, []);

  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current);
  }, [fetchMoreOnBottomReached]);

  return (
    <div>
      <div>
        <div
          onScroll={(e) => fetchMoreOnBottomReached(e.target as HTMLDivElement)}
          ref={tableContainerRef}
        >
          <div className="d-flex flex-row my-3">
            <h5>전체</h5>
            <h5 className="pl-1 text-primary font-weight-bold">
              {totalDBRowCount}
            </h5>
            <h5>명 등록</h5>
          </div>
          <table>
            <thead className="container-fluid">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  className="row mx-0 align-items-center border-top border-bottom border-bottom-2 font-weight-bold"
                  key={headerGroup.id}
                >
                  {headerGroup.headers.map((header) => {
                    return (
                      <th
                        className="list-group-item bg-gray-100 border-0 cursor-pointer select-none text-center"
                        key={header.id}
                        colSpan={header.colSpan}
                        style={{ width: header.getSize() }}
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            {...{
                              className: header.column.getCanSort()
                                ? "cursor-pointer select-none"
                                : "",
                              onClick: header.column.getToggleSortingHandler(),
                            }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: <i className="bi bi-arrow-up-short" />,
                              desc: <i className="bi bi-arrow-down-short" />,
                            }[header.column.getIsSorted() as string] ?? null}
                          </div>
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody className={"container-fluid"}>
              {table.getRowModel().rows.map((row) => {
                return Number(row.id) === editRowId ? (
                  <TableEditRow
                    key={editRowId}
                    id={row.original.id}
                    setEditRowId={setEditRowId}
                    data={row.original}
                    refetch={refetch}
                  />
                ) : (
                  <tr
                    key={row.id}
                    className={"row mx-0 align-items-center border-bottom"}
                  >
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <td
                          key={cell.id}
                          className={"list-group-item border-0 text-center"}
                          style={{ width: cell.column.getSize() }}
                        >
                          {cell.getValue() === 1 && <i className="i-check" />}
                          {cell.getValue() === 0 || cell.getValue() === 1
                            ? ""
                            : flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {newRowData.length > 0
            ? newRowData.map((data) => (
                <TableNewRow key={data.id} id={data.id} refetch={refetch} />
              ))
            : null}
          {totalPage ? <TablePage /> : null}
          <Footer handleRemove={handleRemove} refetch={refetch} />
        </div>
      </div>
    </div>
  );
}

export default Table;
